/**
 * SAVE.DAT codec: parse the regions we understand, preserve everything else byte
 * for byte.
 *
 * **Two games.** This codec reads Fallout 2's SAVE.DAT and Fallout 1's. They
 * share a byte-identical 30051-byte header (only the version word differs: 1.2R
 * against 1.1R) and then diverge in seven places, all of which live in
 * `gamedefs.ts` as a table rather than in this file as branches. Read that
 * module's comment for the full section-by-section comparison; the two
 * differences that shape the *code* here are that Fallout 1 writes the event
 * queue at group 15 — between the PC stats and the traits, both of which the
 * editor writes — instead of at group 24 inside the opaque tail, and that its
 * perk block is a flat 64 words for the dude with no AI-packet section behind
 * it, which removes the two-dimensional search the Fallout 2 anchor has to do.
 *
 * The game is taken from the header's own version bytes, which is the same test
 * each engine uses to refuse the other's saves, so a caller normally does not
 * pass one.
 *
 * The rest of this comment describes the Fallout 2 layout, which remains the
 * reference the code is written against.
 *
 * The authority for this file is `engine/src/loadsave.cc`. The save is a header
 * followed by 27 "function groups" written back to back by `_master_save_list`,
 * each group being whatever its handler chose to emit. There are no lengths, no
 * offsets and no tags: the only way to find group N is to know the exact size of
 * groups 0..N-1. That is why editors get this wrong, and it is why this codec
 * parses forward and then *anchors*.
 *
 * Group order (`_master_save_list`, engine/src/loadsave.cc:~236). Sizes are in
 * bytes; "var" means the size depends on runtime state.
 *
 *    #  handler                      size   what it is
 *    0  _DummyFunc                      0   nothing
 *    1  _SaveObjDudeCid                 4   gDude->cid
 *    2  scriptsSaveGameGlobalVars    4*N    the GVAR array (N from vault13.gam)
 *    3  _GameMap2Slot                 var   int32 count, count NUL-terminated
 *                                           ".SAV" map names, int32 AUTOMAP.DB size
 *    4  scriptsSaveGameGlobalVars    4*N    a second, identical copy of the GVARs
 *                                           (the loader reads it and throws it away —
 *                                           scriptsSkipGameGlobalVars)
 *    5  _obj_save_dude                var   the dude Object, then its inventory tree
 *                                           (recursive), then int32 gCenterTile
 *    6  critterSave                   316   int32 _sneak_working + CritterProtoData
 *    7  killsSave                      76   19 int32 kill counts
 *    8  skillsSave                     16   4 int32 tagged skills
 *    9  randomSave                       0  calls _roll_reset_(), writes nothing
 *   10  perksSave              4*119*party  perk ranks: the dude, then each party slot
 *   11  combatSave                 4|var    int32 gCombatState; the rest only if
 *                                           (state & 1)
 *   12  aiSave                  180*packets AI packets for party protos whose
 *                                           disposition is NONE
 *   13  statsSave                      20   5 int32 PC stats
 *   14  itemsSave                       0   _item_load_ is `return 0` in CE
 *   15  traitsSave                      8   2 int32 selected traits
 *   16  automapSave                     4   int32 gAutomapFlags
 *   17  preferencesSave                80   13 int32, float, 4 int32, 2 float
 *   18  characterEditorSave             5   int32 last level + uint8 free perk
 *   19  wmWorldMap_save               var
 *   20  pipboySave                    var
 *   21  gameMoviesSave                var   one byte per movie
 *   22  skillsUsageSave                216  18*3 int32
 *   23  partyMembersSave              var
 *   24  queueSave                     var
 *   25  interfaceSave                   7   bool, bool, int32 hand, bool
 *   26  _DummyFunc                      0   nothing
 *
 * Everything is big-endian: `_db_fwriteLong` writes the high 16 bits first
 * (engine/src/db.cc).
 *
 * Three sizes are not derivable from the file alone, because they come from game
 * data the save does not carry:
 *
 *   - N, the GVAR count, comes from `data/vault13.gam` (695 in stock Fallout 2).
 *     We recover it by exploiting group 4: the only N for which a block of N
 *     int32s, a well-formed map-name list, and a byte-identical second block all
 *     line up is the real one.
 *   - the dude's inventory tree (group 5) cannot be walked without the item
 *     protos, because an item writes 0, 1 or 2 extra int32s depending on its
 *     proto's item type. We do not walk it. We keep it as an opaque span and find
 *     its end by anchoring on group 6.
 *   - the party-slot count (group 10) and the AI-packet count (group 12) come
 *     from `party.txt` and the loaded protos. We solve for both by requiring the
 *     whole downstream chain — PC stats, traits, automap flags, preferences — to
 *     validate.
 *
 * The anchor is not a guess. It is the unique offset at which twelve consecutive
 * structures all read as sane values; a wrong offset fails within a few words.
 *
 * Round-trip invariant: `serialize(parse(bytes))` is byte-identical to `bytes`.
 * `serialize` re-encodes every typed field from the parsed model rather than
 * copying the original buffer, so byte identity is evidence that the decode is
 * faithful, not merely that we kept a copy.
 */

import {
  AI_PACKET_WORDS,
  CHARACTER_NAME_SIZE,
  COMBAT_STATE_IN_COMBAT,
  DESCRIPTION_SIZE,
  HEADER_SIZE,
  MAP_FILE_NAME_SIZE,
  MAX_PERK_RANK,
  NUM_TAGGED_SKILLS,
  PC_STAT_COUNT,
  PERK_COUNT,
  PREVIEW_SIZE,
  PRIMARY_STAT_COUNT,
  SAVEABLE_STAT_COUNT,
  SAVE_SIGNATURE,
  SIGNATURE_COMPARE_LENGTH,
  SIGNATURE_FIELD_SIZE,
  SKILL_COUNT,
  TRAITS_MAX_SELECTED_COUNT,
} from './constants';
import {
  formatFor,
  gameFromVersion,
  type SaveFormat,
  type SaveGameType,
} from './gamedefs';
import { forGame } from './constants-fo1';

export class SaveParseError extends Error {
  readonly offset: number | undefined;
  constructor(message: string, offset?: number) {
    super(message);
    this.name = 'SaveParseError';
    this.offset = offset;
  }
}

/** A preserved byte range: `[start, end)` in the original file. */
export type Span = { readonly start: number; readonly end: number };

export type SaveHeader = {
  /** The 24-byte signature field, verbatim (only the first 18 bytes are checked). */
  signatureRaw: Uint8Array;
  /** `"FALLOUT SAVE FILE"`, trimmed at the first NUL. */
  signature: string;
  /**
   * `lsgSaveHeaderInSlot` writes VERSION_MAJOR then VERSION_MINOR, but assigns
   * them into fields named the other way round. We name them by what is written.
   */
  versionMajor: number;
  versionMinor: number;
  versionRelease: string;
  characterNameRaw: Uint8Array;
  characterName: string;
  descriptionRaw: Uint8Array;
  description: string;
  /**
   * Wall clock at save time. The writer emits day, month, year; the reader
   * assigns them month, day, year. That swap is an upstream bug — we follow the
   * writer, so these fields mean what they say.
   */
  fileDay: number;
  fileMonth: number;
  fileYear: number;
  /** `local->tm_hour + local->tm_min` — an upstream oddity, not a timestamp. */
  fileTime: number;
  gameMonth: number;
  gameDay: number;
  gameYear: number;
  /** In-game ticks. */
  gameTime: number;
  elevation: number;
  map: number;
  mapFileRaw: Uint8Array;
  /** e.g. `"NCRENT.SAV"`. */
  mapFile: string;
  /** 224x133 8-bit preview, opaque. */
  preview: Uint8Array;
  /** 128 bytes the writer zeroes and the reader skips. Preserved anyway. */
  trailer: Uint8Array;
};

/**
 * The dude's `Object`, as written by `objectWrite` + `objectDataWrite`
 * (engine/src/object.cc, engine/src/proto.cc). 32 int32s, then the inventory.
 */
export type DudeObject = {
  id: number;
  tile: number;
  x: number;
  y: number;
  sx: number;
  sy: number;
  frame: number;
  rotation: number;
  fid: number;
  /** ObjectFlags bitfield. The writer clears OBJECT_NO_SAVE before writing. */
  flags: number;
  elevation: number;
  pid: number;
  cid: number;
  lightDistance: number;
  lightIntensity: number;
  outline: number;
  /** Always -1 for the dude: `_obj_save_dude` forces it. */
  sid: number;
  scriptIndex: number;
  inventoryLength: number;
  inventoryCapacity: number;
  /** The engine writes a literal 0 here (it used to be a pointer). */
  inventoryPointer: number;
  dataFlags: number;
  combatDamageLastTurn: number;
  combatManeuver: number;
  combatAp: number;
  combatResults: number;
  combatAiPacket: number;
  combatTeam: number;
  combatWhoHitMeCid: number;
  /** Current hit points. The engine stores this; nothing recomputes it on load. */
  hp: number;
  radiation: number;
  poison: number;
};

/**
 * `CritterProtoData` for the dude's proto, as written by `protoCritterDataWrite`
 * (engine/src/critter.cc). This is where the character sheet actually lives:
 * SPECIAL, every derived stat, and the raw skill points.
 */
export type CritterData = {
  /** CritterFlags bitfield (sneaking, level-up available, addicted, ...). */
  flags: number;
  /** SAVEABLE_STAT_COUNT entries, indexed by the Stat enum. */
  baseStats: Int32Array;
  /** SAVEABLE_STAT_COUNT entries. `critterGetStat` returns base + bonus + modifiers. */
  bonusStats: Int32Array;
  /** SKILL_COUNT raw skill *points*, not percentages. See `skillGetValue`. */
  skills: Int32Array;
  bodyType: number;
  /** The proto's XP award for killing this critter. Not the player's XP. */
  experience: number;
  killType: number;
  /**
   * Fallout 2 only. `CritterProtoData` ends with `damageType` in
   * engine/src/proto_types.h and does not in fallout1-ce's
   * (proto_types.h:300-308), which is the whole four-byte difference between a
   * 376-byte and a 372-byte critter section. Null on a Fallout 1 save, and the
   * serializer omits the word rather than writing a zero.
   */
  damageType: number | null;
};

export type SaveLayout = {
  header: Span;
  dudeCid: Span;
  gvars: Span;
  mapList: Span;
  gvarsMirror: Span;
  dudeObject: Span;
  /** The dude's inventory tree. Opaque by design — see the module comment. */
  inventory: Span;
  centerTile: Span;
  critter: Span;
  kills: Span;
  taggedSkills: Span;
  perks: Span;
  combatState: Span;
  /** AI packets. Opaque: we only need their size. */
  aiPackets: Span;
  pcStats: Span;
  traits: Span;
  automapFlags: Span;
  /** Groups 17..26 — preferences through interface. Opaque. */
  tail: Span;
  /** Group 24 when its self-describing event stream can be located. */
  queue: Span | null;
};

/** One entry from queueSave. Payload words are event-type specific. */
export type QueueEvent = {
  /** Absolute game tick at which the event fires. */
  time: number;
  type: number;
  /** Object id, or -2 for no owner. */
  ownerId: number;
  /** Raw big-endian int32 payload, preserved and re-emitted verbatim. */
  data: Int32Array;
};

export type QueueLayout = {
  /** Byte offsets within tailBlob, not the whole SAVE.DAT. */
  start: number;
  end: number;
};

export type SaveDat = {
  /** Which engine wrote this file, from its header version bytes. */
  game: SaveGameType;
  /** The section layout that game uses. See `gamedefs.ts`. */
  format: SaveFormat;
  byteLength: number;
  /** Original bytes used as the immutable base for surgical serialization. */
  originalBytes: Uint8Array;
  header: SaveHeader;
  dudeCid: number;
  /** Game globals. Index is the declaration order in `data/vault13.gam`. */
  gvars: Int32Array;
  /**
   * The bytes each GVAR block carries *after* the array. Fallout 1's
   * `scr_game_save` (fallout1-ce scripts.cc:1526) appends the one-byte
   * `water_movie_play_flag`; Fallout 2 appends nothing, so this is empty there.
   * Preserved verbatim — it is engine state the editor has no business moving.
   * The mirror block carries its own copy, kept separately so a file where the
   * two disagree still round-trips.
   */
  gvarTrailer: Uint8Array;
  gvarMirrorTrailer: Uint8Array;
  /**
   * The second GVAR copy, when it differs from the first. Normally null: the
   * engine writes the same array twice. Kept so an odd save still round-trips.
   */
  gvarsMirror: Int32Array | null;
  /** The `.SAV` map files this slot carries, in the order the engine listed them. */
  mapFiles: string[];
  /** Size of AUTOMAP.DB at save time. */
  automapDbSize: number;
  dude: DudeObject;
  /** Opaque: the dude's nested inventory objects. Never rewritten by this codec. */
  inventoryBlob: Uint8Array;
  centerTile: number;
  sneakWorking: number;
  critter: CritterData;
  /** KILL_TYPE_COUNT counters, indexed by the KillType enum. */
  kills: Int32Array;
  /** NUM_TAGGED_SKILLS skill ids, -1 for an unused slot. */
  taggedSkills: Int32Array;
  /**
   * Perk ranks, `partySlotCount * PERK_COUNT`. Row 0 is the dude; rows 1.. are
   * the party slots declared in `party.txt`, in that file's order.
   */
  perks: Int32Array;
  partySlotCount: number;
  combatState: number;
  /** Opaque AI packets. Their count is `byteLength / (45*4)`. */
  aiPacketsBlob: Uint8Array;
  /** PC_STAT_COUNT entries: unspent skill points, level, XP, reputation, karma. */
  pcStats: Int32Array;
  /** TRAITS_MAX_SELECTED_COUNT trait ids, -1 for an unused slot. */
  traits: Int32Array;
  automapFlags: number;
  /** Opaque: groups 17..26. */
  tailBlob: Uint8Array;
  /** Parsed group 24. Null only when the queue stream is ambiguous or malformed. */
  queueEvents: QueueEvent[] | null;
  queueLayout: QueueLayout | null;
  layout: SaveLayout;
};

export type ParseOptions = {
  /**
   * Force a game rather than taking it from the header's version bytes. Only
   * needed for a file whose header has been rewritten; normal callers omit it.
   */
  game?: SaveGameType;
  /**
   * The GVAR count, if you know it (695 for stock Fallout 2). Supply it when the
   * two GVAR blocks in a file are not identical, which defeats auto-detection.
   */
  gvarCount?: number;
  /** Stop the anchor search after this many bytes past the dude object header. */
  maxInventoryBytes?: number;
  /** Maximum auto-detected global count; prevents hostile files causing a huge scan. */
  maxGvarCount?: number;
};

const DEFAULT_MAX_INVENTORY_BYTES = 1 << 20;
const MAX_SAVE_DAT_BYTES = 64 << 20;
const DEFAULT_MAX_GVAR_COUNT = 16_384;
const MAX_PARTY_SLOTS = 128;
const MAX_AI_PACKETS = 128;
const INTERFACE_SAVE_BYTES = 7;
const MAX_QUEUE_EVENTS = 4096;

// The per-event-type payload sizes now live in `gamedefs.ts`, because the two
// engines have different event enums (Fallout 2 adds GSOUND_SFX_EVENT).

const latin1 = new TextDecoder('latin1');

function cstr(bytes: Uint8Array): string {
  const nul = bytes.indexOf(0);
  return latin1.decode(nul === -1 ? bytes : bytes.subarray(0, nul));
}

/** Write `text` into a fixed-size field, NUL-padded, truncating if it overflows. */
export function fixedField(text: string, size: number): Uint8Array {
  const out = new Uint8Array(size);
  for (let i = 0; i < text.length && i < size; i++) {
    out[i] = text.charCodeAt(i) & 0xff;
  }
  return out;
}

class Reader {
  readonly view: DataView;
  readonly bytes: Uint8Array;
  offset: number;

  constructor(bytes: Uint8Array, offset = 0) {
    this.bytes = bytes;
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    this.offset = offset;
  }

  need(n: number, what: string): void {
    if (this.offset + n > this.bytes.length) {
      throw new SaveParseError(`truncated: need ${n} bytes for ${what}`, this.offset);
    }
  }

  i32(): number {
    this.need(4, 'int32');
    const v = this.view.getInt32(this.offset, false);
    this.offset += 4;
    return v;
  }

  u32(): number {
    this.need(4, 'uint32');
    const v = this.view.getUint32(this.offset, false);
    this.offset += 4;
    return v;
  }

  i16(): number {
    this.need(2, 'int16');
    const v = this.view.getInt16(this.offset, false);
    this.offset += 2;
    return v;
  }

  u8(): number {
    this.need(1, 'uint8');
    return this.bytes[this.offset++]!;
  }

  raw(n: number, what: string): Uint8Array {
    this.need(n, what);
    const v = this.bytes.slice(this.offset, this.offset + n);
    this.offset += n;
    return v;
  }

  i32List(n: number): Int32Array {
    const out = new Int32Array(n);
    for (let i = 0; i < n; i++) out[i] = this.i32();
    return out;
  }
}

class Writer {
  private parts: Uint8Array[] = [];
  private length = 0;

  push(bytes: Uint8Array): void {
    this.parts.push(bytes);
    this.length += bytes.length;
  }

  i32(value: number): void {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setInt32(0, value | 0, false);
    this.push(b);
  }

  u32(value: number): void {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, value >>> 0, false);
    this.push(b);
  }

  i16(value: number): void {
    const b = new Uint8Array(2);
    new DataView(b.buffer).setInt16(0, (value << 16) >> 16, false);
    this.push(b);
  }

  u8(value: number): void {
    this.push(Uint8Array.of(value & 0xff));
  }

  i32List(values: ArrayLike<number>): void {
    const b = new Uint8Array(values.length * 4);
    const dv = new DataView(b.buffer);
    for (let i = 0; i < values.length; i++) dv.setInt32(i * 4, values[i]! | 0, false);
    this.push(b);
  }

  get size(): number {
    return this.length;
  }

  finish(): Uint8Array {
    const out = new Uint8Array(this.length);
    let at = 0;
    for (const part of this.parts) {
      out.set(part, at);
      at += part.length;
    }
    return out;
  }
}

function parseHeader(r: Reader): SaveHeader {
  const signatureRaw = r.raw(SIGNATURE_FIELD_SIZE, 'signature');
  const signature = cstr(signatureRaw);
  if (signature.slice(0, SIGNATURE_COMPARE_LENGTH) !== SAVE_SIGNATURE.slice(0, SIGNATURE_COMPARE_LENGTH)) {
    throw new SaveParseError(`not a Fallout 2 SAVE.DAT (signature is ${JSON.stringify(signature)})`, 0);
  }

  const versionMajor = r.i16();
  const versionMinor = r.i16();
  const versionRelease = String.fromCharCode(r.u8());

  const characterNameRaw = r.raw(CHARACTER_NAME_SIZE, 'character name');
  const descriptionRaw = r.raw(DESCRIPTION_SIZE, 'description');

  const fileDay = r.i16();
  const fileMonth = r.i16();
  const fileYear = r.i16();
  const fileTime = r.i32();

  const gameMonth = r.i16();
  const gameDay = r.i16();
  const gameYear = r.i16();
  const gameTime = r.u32();

  const elevation = r.i16();
  const map = r.i16();
  const mapFileRaw = r.raw(MAP_FILE_NAME_SIZE, 'map file name');

  const preview = r.raw(PREVIEW_SIZE, 'preview');
  const trailer = r.raw(128, 'header trailer');

  return {
    signatureRaw,
    signature,
    versionMajor,
    versionMinor,
    versionRelease,
    characterNameRaw,
    characterName: cstr(characterNameRaw),
    descriptionRaw,
    description: cstr(descriptionRaw),
    fileDay,
    fileMonth,
    fileYear,
    fileTime,
    gameMonth,
    gameDay,
    gameYear,
    gameTime,
    elevation,
    map,
    mapFileRaw,
    mapFile: cstr(mapFileRaw),
    preview,
    trailer,
  };
}

/**
 * Each engine's loader refuses anything but its own version — `lsgLoadHeaderInSlot`
 * wants 1.2R, fallout1-ce's `LoadHeader` wants 1.1R. We do not refuse (a codec
 * that cannot read the file it is asked to fix is useless) but we say so, and
 * the shell drops to expert mode when this is false.
 *
 * `game` defaults to Fallout 2 so existing callers keep their exact behaviour.
 * Pass a save's own `game` to ask "is this the version *its* engine accepts".
 */
export function headerVersionIsSupported(header: SaveHeader, game: SaveGameType = 'fo2'): boolean {
  const format = formatFor(game);
  return (
    header.versionMajor === format.versionMajor
    && header.versionMinor === format.versionMinor
    && header.versionRelease === format.versionRelease
  );
}

type MapListResult = { mapFiles: string[]; automapDbSize: number; end: number };

/**
 * Group 3 (`_GameMap2Slot`): int32 count, `count` NUL-terminated names, int32 size.
 * Returns null rather than throwing — the GVAR-count search calls this
 * speculatively at many offsets.
 */
function tryMapList(bytes: Uint8Array, view: DataView, start: number): MapListResult | null {
  if (start + 4 > bytes.length) return null;
  const count = view.getInt32(start, false);
  // `_GameMap2Slot` fails the save outright when the list is empty, so a real
  // file always has at least one. Stock playthroughs sit in the low tens.
  if (count <= 0 || count > 512) return null;

  let at = start + 4;
  const mapFiles: string[] = [];
  for (let i = 0; i < count; i++) {
    const nameStart = at;
    while (at < bytes.length && bytes[at] !== 0) {
      const c = bytes[at]!;
      if (c < 0x20 || c > 0x7e) return null;
      at++;
    }
    if (at >= bytes.length) return null;
    const length = at - nameStart;
    // File names come from `fileNameListInit` over "MAPS\*.SAV" — 8.3 plus the
    // dot, so twelve characters is the ceiling. Zero-length is impossible.
    if (length === 0 || length > 12) return null;
    mapFiles.push(latin1.decode(bytes.subarray(nameStart, at)));
    at++;
  }

  if (at + 4 > bytes.length) return null;
  const automapDbSize = view.getInt32(at, false);
  if (automapDbSize < 0 || automapDbSize > 1 << 26) return null;
  at += 4;

  return { mapFiles, automapDbSize, end: at };
}

type GvarSolution = {
  count: number;
  gvarsStart: number;
  mapList: MapListResult;
  mirrorStart: number;
  end: number;
  mirrorDiffers: boolean;
};

/**
 * Recover N, the GVAR count, from the file's own structure.
 *
 * Groups 2, 3 and 4 form a sandwich: N int32s, a map-name list, N int32s again
 * with the same contents. For a candidate N to survive it must place a
 * well-formed map list immediately after the first block and an identical second
 * block immediately after that. Two independent structures have to agree, so a
 * wrong N essentially never survives; the search takes the first N that does.
 */
function solveGvarCount(
  bytes: Uint8Array,
  view: DataView,
  gvarsStart: number,
  hint: number | undefined,
  maximum = DEFAULT_MAX_GVAR_COUNT,
  /**
   * Bytes each block appends after the array (Fallout 1's
   * `water_movie_play_flag`; zero for Fallout 2). They sit between the first
   * block and the map list, and between the map list and the mirror, so the
   * search has to step over them or every candidate N misses by one byte.
   */
  trailerBytes = 0,
): GvarSolution {
  const candidates: number[] = [];
  if (hint !== undefined) {
    candidates.push(hint);
  } else {
    for (let n = 1; gvarsStart + 4 * n + 8 + 2 * trailerBytes <= bytes.length && n <= maximum; n++) {
      candidates.push(n);
    }
  }

  let solution: GvarSolution | null = null;
  for (const count of candidates) {
    const afterFirst = gvarsStart + 4 * count + trailerBytes;
    const mapList = tryMapList(bytes, view, afterFirst);
    if (!mapList) continue;

    const mirrorStart = mapList.end;
    const end = mirrorStart + 4 * count + trailerBytes;
    if (end > bytes.length) continue;

    let differs = false;
    for (let i = 0; i < count; i++) {
      if (view.getInt32(gvarsStart + 4 * i, false) !== view.getInt32(mirrorStart + 4 * i, false)) {
        differs = true;
        break;
      }
    }
    // Without a hint we insist the copies match, because that is the property
    // that makes the answer unique. With a hint the caller has taken ownership
    // of the number and we preserve whatever is there.
    if (differs && hint === undefined) continue;

    const found = { count, gvarsStart, mapList, mirrorStart, end, mirrorDiffers: differs };
    if (hint !== undefined) return found;
    if (solution) {
      throw new SaveParseError(
        `GVAR count is ambiguous (${solution.count} and ${count} both fit the duplicated blocks)`,
        gvarsStart,
      );
    }
    solution = found;
  }

  if (solution) return solution;

  throw new SaveParseError(
    hint === undefined
      ? 'could not recover the GVAR count: no N places a map list and an identical second GVAR block'
      : `GVAR count hint ${hint} does not produce a well-formed map list`,
    gvarsStart,
  );
}

function parseDudeObject(r: Reader): DudeObject {
  return {
    id: r.i32(),
    tile: r.i32(),
    x: r.i32(),
    y: r.i32(),
    sx: r.i32(),
    sy: r.i32(),
    frame: r.i32(),
    rotation: r.i32(),
    fid: r.i32(),
    flags: r.u32(),
    elevation: r.i32(),
    pid: r.i32(),
    cid: r.i32(),
    lightDistance: r.i32(),
    lightIntensity: r.i32(),
    outline: r.i32(),
    sid: r.i32(),
    scriptIndex: r.i32(),
    inventoryLength: r.i32(),
    inventoryCapacity: r.i32(),
    inventoryPointer: r.i32(),
    dataFlags: r.i32(),
    combatDamageLastTurn: r.i32(),
    combatManeuver: r.i32(),
    combatAp: r.i32(),
    combatResults: r.i32(),
    combatAiPacket: r.i32(),
    combatTeam: r.i32(),
    combatWhoHitMeCid: r.i32(),
    hp: r.i32(),
    radiation: r.i32(),
    poison: r.i32(),
  };
}

/** Object header words written by objectWrite + objectDataWrite for a critter. */
const DUDE_OBJECT_WORDS = 32;

type Anchor = {
  /** Offset of group 6 (`critterSave`). */
  start: number;
  sneakWorking: number;
  critter: CritterData;
  kills: Int32Array;
  taggedSkills: Int32Array;
  perks: Int32Array;
  partySlotCount: number;
  combatState: number;
  aiPacketsBlob: Uint8Array;
  pcStats: Int32Array;
  traits: Int32Array;
  automapFlags: number;
  /** Offset just past the automap group — where the opaque tail begins. */
  tailStart: number;
  /**
   * Fallout 1 only: the queue, which its `master_save_list` writes at group 15,
   * in between the PC stats and the traits. Null on Fallout 2, where the queue
   * is group 24 and is found afterwards by anchoring backwards from EOF.
   */
  queue: { events: QueueEvent[]; span: Span } | null;
  spans: {
    critter: Span;
    kills: Span;
    taggedSkills: Span;
    perks: Span;
    combatState: Span;
    aiPackets: Span;
    pcStats: Span;
    traits: Span;
    automapFlags: Span;
  };
};

/**
 * Group 17 (`preferencesSave`). We never expose these, but they make the
 * anchor unambiguous: thirteen enums with tight ranges, a delay float the
 * options screen clamps to 1..6, four volumes, and two floats that are ratios
 * near 1. A random offset does not satisfy all of that.
 */
function preferencesLookSane(view: DataView, at: number, length: number): boolean {
  if (at + 80 > length) return false;
  const i = (k: number) => view.getInt32(at + 4 * k, false);
  const f = (k: number) => view.getFloat32(at + 4 * k, false);

  const enums = [
    [i(0), 0, 3], // game difficulty
    [i(1), 0, 3], // combat difficulty
    [i(2), 0, 3], // violence level
    [i(3), 0, 3], // target highlight
    [i(4), 0, 1], // combat looks
    [i(5), 0, 1], // combat messages
    [i(6), 0, 1], // combat taunts
    [i(7), 0, 1], // language filter
    [i(8), 0, 1], // running
    [i(9), 0, 1], // subtitles
    [i(10), 0, 1], // item highlight
    [i(11), 0, 100], // combat speed
    [i(12), 0, 1], // player speedup
  ];
  for (const [value, min, max] of enums) {
    if (value! < min! || value! > max!) return false;
  }

  const textBaseDelay = f(13);
  if (!(textBaseDelay >= 1 && textBaseDelay <= 6)) return false;
  for (let k = 14; k <= 17; k++) {
    const volume = i(k);
    if (volume < 0 || volume > 32767) return false;
  }
  const brightness = f(18);
  if (!(brightness >= 1 && brightness <= 1.2)) return false;
  const mouseSensitivity = f(19);
  if (!(mouseSensitivity >= 1 && mouseSensitivity <= 2.5)) return false;

  // Group 18: characterEditorSave — int32 last level, uint8 free perk.
  if (at + 85 > length) return false;
  const lastLevel = view.getInt32(at + 80, false);
  return lastLevel >= 0 && lastLevel <= 99;
}

/**
 * Try to read the character block starting at `start`. Returns null the moment
 * anything reads as nonsense, which is what makes the scan safe.
 *
 * Everything up to and including the tagged skills is common to both games; the
 * two engines diverge from the perk block onward, and the tail of this function
 * forks accordingly. The `format` table supplies every size that moved.
 */
function tryAnchor(
  bytes: Uint8Array,
  view: DataView,
  start: number,
  format: SaveFormat,
): Anchor | null {
  const length = bytes.length;
  const statBounds = forGame(format.game).statBounds;
  const killTypeCount = format.killTypeCount;
  const traitCount = format.traitCount;
  const criticalWords = format.critterHasDamageType ? 6 : 5;
  const words = (
    SAVEABLE_STAT_COUNT * 2 + SKILL_COUNT + criticalWords + killTypeCount + NUM_TAGGED_SKILLS
  );
  if (start + 4 * words > length) return null;

  const i = (at: number) => view.getInt32(at, false);

  let at = start;
  const sneakWorking = i(at);
  if (sneakWorking !== 0 && sneakWorking !== 1) return null;
  at += 4;

  const critterStart = at;
  const flags = i(at);
  at += 4;
  const baseStats = new Int32Array(SAVEABLE_STAT_COUNT);
  for (let k = 0; k < SAVEABLE_STAT_COUNT; k++, at += 4) baseStats[k] = i(at);
  const bonusStats = new Int32Array(SAVEABLE_STAT_COUNT);
  for (let k = 0; k < SAVEABLE_STAT_COUNT; k++, at += 4) bonusStats[k] = i(at);
  const skills = new Int32Array(SKILL_COUNT);
  for (let k = 0; k < SKILL_COUNT; k++, at += 4) skills[k] = i(at);
  const bodyType = i(at);
  at += 4;
  const experience = i(at);
  at += 4;
  const killType = i(at);
  at += 4;
  // Fallout 1's `critter_write_data` (critter.cc:1021) stops here. Fallout 2's
  // writes one more word, and that is the entire size difference.
  let damageType: number | null = null;
  if (format.critterHasDamageType) {
    damageType = i(at);
    at += 4;
    if (damageType < 0 || damageType > 8) return null;
  }

  // Validate the whole stat array, not just SPECIAL. A shifted offset inside
  // inventory or another critter sub-array can otherwise look like seven sane
  // primary stats while placing nonsense in derived fields.
  for (let k = 0; k < SAVEABLE_STAT_COUNT; k++) {
    const v = baseStats[k]!;
    const [minimum, maximum] = statBounds[k]!;
    // Mods can raise primary caps; retain headroom there, while derived fields
    // use the engine's declared bounds.
    if (k < PRIMARY_STAT_COUNT) {
      if (v < 1 || v > 20) return null;
    } else if (v < minimum || v > maximum) return null;
    if (bonusStats[k]! < -999 || bonusStats[k]! > 999) return null;
  }
  if (bodyType < 0 || bodyType > 2) return null;
  if (experience < 0 || experience > 100_000_000) return null;
  if (killType < 0 || killType >= killTypeCount) return null;
  // Raw skill points, floored at -128 by the engine; the practical ceiling is
  // the 300% skill cap divided by the smallest multiplier.
  for (let k = 0; k < SKILL_COUNT; k++) {
    const v = skills[k]!;
    if (v < -128 || v > 400) return null;
  }
  const critterEnd = at;

  const killsStart = at;
  const kills = new Int32Array(killTypeCount);
  for (let k = 0; k < killTypeCount; k++, at += 4) {
    const v = i(at);
    if (v < 0 || v > 1_000_000) return null;
    kills[k] = v;
  }
  const killsEnd = at;

  const taggedStart = at;
  const taggedSkills = new Int32Array(NUM_TAGGED_SKILLS);
  for (let k = 0; k < NUM_TAGGED_SKILLS; k++, at += 4) {
    const v = i(at);
    if (v < -1 || v >= SKILL_COUNT) return null;
    taggedSkills[k] = v;
  }
  const taggedEnd = at;

  const critter: CritterData = {
    flags, baseStats, bonusStats, skills, bodyType, experience, killType, damageType,
  };
  const commonSpans = {
    critter: { start: critterStart, end: critterEnd },
    kills: { start: killsStart, end: killsEnd },
    taggedSkills: { start: taggedStart, end: taggedEnd },
  };

  if (format.queueBeforeTraits) {
    return tryAnchorFo1(bytes, view, {
      start, format, sneakWorking, critter, kills, taggedSkills, perksStart: at, commonSpans,
    });
  }

  const perksStart = at;
  for (let party = 1; party <= MAX_PARTY_SLOTS; party++) {
    const perkWords = party * PERK_COUNT;
    const perksEnd = perksStart + 4 * perkWords;
    if (perksEnd + 4 > length) break;

    // Ranks are small non-negative integers. Once a word breaks that, no larger
    // party count can work either.
    let ranksOk = true;
    for (let k = (party - 1) * PERK_COUNT; k < perkWords; k++) {
      const v = i(perksStart + 4 * k);
      if (v < 0 || v > MAX_PERK_RANK) {
        ranksOk = false;
        break;
      }
    }
    if (!ranksOk) break;

    const combatState = i(perksEnd);
    // A save taken mid-combat writes a variable-length combat list we do not
    // model. Those files parse only up to the perks; we bail rather than guess.
    if ((combatState & COMBAT_STATE_IN_COMBAT) !== 0) continue;
    if (combatState < 0 || combatState > 0xff) continue;

    const aiStart = perksEnd + 4;
    for (let packets = 0; packets <= MAX_AI_PACKETS; packets++) {
      const statsStart = aiStart + packets * AI_PACKET_WORDS * 4;
      const traitsStart = statsStart + PC_STAT_COUNT * 4;
      const automapStart = traitsStart + TRAITS_MAX_SELECTED_COUNT * 4;
      const prefsStart = automapStart + 4;
      if (prefsStart + 80 > length) break;

      const unspent = i(statsStart);
      const level = i(statsStart + 4);
      const xp = i(statsStart + 8);
      const reputation = i(statsStart + 12);
      const karma = i(statsStart + 16);
      if (unspent < 0 || unspent > 10000) continue;
      if (level < 1 || level > 99) continue;
      if (xp < 0 || xp > 100_000_000) continue;
      if (Math.abs(reputation) > 1_000_000) continue;
      if (Math.abs(karma) > 100_000_000) continue;

      const t0 = i(traitsStart);
      const t1 = i(traitsStart + 4);
      if (t0 < -1 || t0 >= traitCount) continue;
      if (t1 < -1 || t1 >= traitCount) continue;

      const automapFlags = i(automapStart);
      if (automapFlags < 0 || automapFlags > 0xffff) continue;

      if (!preferencesLookSane(view, prefsStart, length)) continue;

      const perks = new Int32Array(perkWords);
      for (let k = 0; k < perkWords; k++) perks[k] = i(perksStart + 4 * k);
      const pcStats = new Int32Array([unspent, level, xp, reputation, karma]);
      const traits = new Int32Array([t0, t1]);

      return {
        start,
        sneakWorking,
        critter,
        kills,
        taggedSkills,
        perks,
        partySlotCount: party,
        combatState,
        aiPacketsBlob: bytes.slice(aiStart, statsStart),
        pcStats,
        traits,
        automapFlags,
        tailStart: prefsStart,
        queue: null,
        spans: {
          ...commonSpans,
          perks: { start: perksStart, end: perksEnd },
          combatState: { start: perksEnd, end: aiStart },
          aiPackets: { start: aiStart, end: statsStart },
          pcStats: { start: statsStart, end: traitsStart },
          traits: { start: traitsStart, end: automapStart },
          automapFlags: { start: automapStart, end: prefsStart },
        },
      };
    }
  }

  return null;
}

/**
 * The Fallout 1 tail of `tryAnchor`: groups 10..17.
 *
 * Structurally simpler than Fallout 2's in one way and harder in another.
 * Simpler because there is nothing to solve for — `perk_save` (perk.cc:173)
 * writes exactly `PERK_COUNT` (64) words for the dude and no party rows, and
 * `combat_ai_save` (combatai.cc:281) is `return 0`, so the offsets from the
 * perks to the PC stats are fixed. Harder because `queue_save` sits at group 15,
 * between the PC stats and the traits, so the traits cannot be found without
 * walking the queue — where Fallout 2 reads its traits four words after the
 * stats and locates its queue later, from the far end of the file.
 *
 * Walking is safe: the queue is self-describing (a count, then records of
 * `time, type, ownerId` plus a payload whose size is fixed per event type), and
 * every field is range-checked. The traits, automap flags and options block
 * immediately after it are the confirmation that the walk landed correctly.
 */
function tryAnchorFo1(
  bytes: Uint8Array,
  view: DataView,
  ctx: {
    start: number;
    format: SaveFormat;
    sneakWorking: number;
    critter: CritterData;
    kills: Int32Array;
    taggedSkills: Int32Array;
    perksStart: number;
    commonSpans: { critter: Span; kills: Span; taggedSkills: Span };
  },
): Anchor | null {
  const { format, perksStart } = ctx;
  const length = bytes.length;
  const i = (at: number) => view.getInt32(at, false);

  const perkWords = format.perkCount;
  const perksEnd = perksStart + 4 * perkWords;
  if (perksEnd + 4 > length) return null;
  for (let k = 0; k < perkWords; k++) {
    const v = i(perksStart + 4 * k);
    if (v < 0 || v > MAX_PERK_RANK) return null;
  }

  const combatState = i(perksEnd);
  // `combat_save` (combat.cc:1696) writes a variable-length combatant list when
  // `isInCombat()`, exactly as Fallout 2's does. Same refusal, same reason.
  if ((combatState & COMBAT_STATE_IN_COMBAT) !== 0) return null;
  if (combatState < 0 || combatState > 0xff) return null;

  // `combat_ai_save` writes nothing, so the AI span is empty rather than absent
  // — an empty span keeps every downstream consumer (spans, serializer, the
  // diff-locality tests) uniform across the two games.
  const aiStart = perksEnd + 4;
  const statsStart = aiStart;
  const queueStart = statsStart + PC_STAT_COUNT * 4;
  if (queueStart + 4 > length) return null;

  const unspent = i(statsStart);
  const level = i(statsStart + 4);
  const xp = i(statsStart + 8);
  const reputation = i(statsStart + 12);
  const karma = i(statsStart + 16);
  if (unspent < 0 || unspent > 10000) return null;
  if (level < 1 || level > format.pcLevelMax) return null;
  if (xp < 0 || xp > 100_000_000) return null;
  if (Math.abs(reputation) > 1_000_000) return null;
  if (Math.abs(karma) > 100_000_000) return null;

  const walked = walkQueue(view, queueStart, length, format);
  if (!walked) return null;

  const traitsStart = walked.end;
  const automapStart = traitsStart + TRAITS_MAX_SELECTED_COUNT * 4;
  const optionsStart = automapStart + 4;
  if (optionsStart + 80 > length) return null;

  const t0 = i(traitsStart);
  const t1 = i(traitsStart + 4);
  if (t0 < -1 || t0 >= format.traitCount) return null;
  if (t1 < -1 || t1 >= format.traitCount) return null;

  const automapFlags = i(automapStart);
  if (automapFlags < 0 || automapFlags > 0xffff) return null;

  // `save_options` (options.cc:1941) and `editor_save` (editor.cc:5170) write
  // the same twenty words and five bytes, in the same order, as Fallout 2's
  // groups 17 and 18 — so the same sanity test confirms the anchor.
  if (!preferencesLookSane(view, optionsStart, length)) return null;

  const perks = new Int32Array(perkWords);
  for (let k = 0; k < perkWords; k++) perks[k] = i(perksStart + 4 * k);

  return {
    start: ctx.start,
    sneakWorking: ctx.sneakWorking,
    critter: ctx.critter,
    kills: ctx.kills,
    taggedSkills: ctx.taggedSkills,
    perks,
    partySlotCount: 1,
    combatState,
    aiPacketsBlob: new Uint8Array(0),
    pcStats: new Int32Array([unspent, level, xp, reputation, karma]),
    traits: new Int32Array([t0, t1]),
    automapFlags,
    tailStart: optionsStart,
    queue: { events: walked.events, span: { start: queueStart, end: traitsStart } },
    spans: {
      ...ctx.commonSpans,
      perks: { start: perksStart, end: perksEnd },
      combatState: { start: perksEnd, end: aiStart },
      aiPackets: { start: aiStart, end: statsStart },
      pcStats: { start: statsStart, end: queueStart },
      traits: { start: traitsStart, end: automapStart },
      automapFlags: { start: automapStart, end: optionsStart },
    },
  };
}

/**
 * Read one `queue_save` / `queueSave` stream forward from `start`.
 *
 * Both engines write it identically: `int32 count`, then per record
 * `time, type, ownerId` and the event type's own payload, which is a fixed
 * number of int32s per type (`gEventTypeDescriptions` / `q_func` write procs).
 * A type with no write proc carries none.
 *
 * Returns null on anything that is not a well-formed stream. The monotonic time
 * check is the engine's own invariant: `queue_add` inserts in time order and the
 * writer walks the list head to tail.
 */
function walkQueue(
  view: DataView,
  start: number,
  limit: number,
  format: SaveFormat,
): { events: QueueEvent[]; end: number } | null {
  if (start + 4 > limit) return null;
  const count = view.getInt32(start, false);
  if (count < 0 || count > MAX_QUEUE_EVENTS) return null;

  let at = start + 4;
  let previousTime = 0;
  const events: QueueEvent[] = [];
  for (let index = 0; index < count; index++) {
    if (at + 12 > limit) return null;
    const time = view.getUint32(at, false);
    const type = view.getInt32(at + 4, false);
    const ownerId = view.getInt32(at + 8, false);
    at += 12;
    const words = format.queueEventDataWords[type];
    if (words === undefined || ownerId < -2 || (index > 0 && time < previousTime)) return null;
    if (at + words * 4 > limit) return null;
    const data = new Int32Array(words);
    for (let word = 0; word < words; word++, at += 4) data[word] = view.getInt32(at, false);
    events.push({ time, type, ownerId, data });
    previousTime = time;
  }
  return { events, end: at };
}

/**
 * Locate group 24 from the fixed seven-byte interface group that follows it.
 * The groups before queueSave are variable length, but queueSave describes its
 * own record count and every CE event type has a fixed wire payload. Requiring
 * the stream to land exactly seven bytes before EOF makes this an anchor, not a
 * heuristic. Multiple valid anchors are treated as ambiguous and left opaque.
 */
function parseQueueFromTail(
  bytes: Uint8Array,
  view: DataView,
  tailStart: number,
  format: SaveFormat,
): { events: QueueEvent[]; layout: QueueLayout; span: Span } | null {
  const queueEnd = bytes.length - INTERFACE_SAVE_BYTES;
  if (queueEnd < tailStart + 4) return null;

  const matches: { events: QueueEvent[]; start: number }[] = [];
  for (let start = tailStart; start + 4 <= queueEnd; start++) {
    const walked = walkQueue(view, start, queueEnd, format);
    if (walked && walked.end === queueEnd) matches.push({ events: walked.events, start });
  }

  if (matches.length !== 1) return null;
  const match = matches[0]!;
  return {
    events: match.events,
    layout: { start: match.start - tailStart, end: queueEnd - tailStart },
    span: { start: match.start, end: queueEnd },
  };
}

export function parseSaveDat(input: Uint8Array | ArrayBuffer, options: ParseOptions = {}): SaveDat {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.length > MAX_SAVE_DAT_BYTES) {
    throw new SaveParseError(`SAVE.DAT is ${bytes.length} bytes; the safety limit is ${MAX_SAVE_DAT_BYTES}`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  const r = new Reader(bytes);
  const header = parseHeader(r);
  if (r.offset !== HEADER_SIZE) {
    throw new SaveParseError(`header ended at ${r.offset}, expected ${HEADER_SIZE}`, r.offset);
  }

  // The game is the header's own version word, which is exactly what each
  // engine's loader tests before it will touch the file. An unrecognised
  // version falls through to Fallout 2, as it did before this codec knew there
  // was a second game — `headerVersionIsSupported` is what says so.
  const game: SaveGameType =
    options.game ?? gameFromVersion(header.versionMajor, header.versionMinor, header.versionRelease) ?? 'fo2';
  const format = formatFor(game);

  const dudeCidStart = r.offset;
  const dudeCid = r.i32();

  const gvarsStart = r.offset;
  const solution = solveGvarCount(
    bytes, view, gvarsStart, options.gvarCount, options.maxGvarCount, format.gvarTrailerBytes,
  );
  const gvars = new Int32Array(solution.count);
  for (let k = 0; k < solution.count; k++) gvars[k] = view.getInt32(gvarsStart + 4 * k, false);
  let gvarsMirror: Int32Array | null = null;
  if (solution.mirrorDiffers) {
    gvarsMirror = new Int32Array(solution.count);
    for (let k = 0; k < solution.count; k++) {
      gvarsMirror[k] = view.getInt32(solution.mirrorStart + 4 * k, false);
    }
  }
  const gvarsEnd = gvarsStart + 4 * solution.count;
  const mirrorEnd = solution.mirrorStart + 4 * solution.count;
  const gvarTrailer = bytes.slice(gvarsEnd, gvarsEnd + format.gvarTrailerBytes);
  const gvarMirrorTrailer = bytes.slice(mirrorEnd, mirrorEnd + format.gvarTrailerBytes);

  const dudeStart = solution.end;
  const dudeReader = new Reader(bytes, dudeStart);
  const dude = parseDudeObject(dudeReader);
  const dudeHeaderEnd = dudeStart + DUDE_OBJECT_WORDS * 4;
  if (dudeReader.offset !== dudeHeaderEnd) {
    throw new SaveParseError('dude object header size mismatch', dudeReader.offset);
  }

  // The inventory tree is unwalkable without protos. Find where it ends by
  // anchoring on group 6 instead. `centerTile` is the last thing group 5 writes,
  // so it is the four bytes immediately before the anchor.
  const limit = Math.min(
    bytes.length,
    dudeHeaderEnd + (options.maxInventoryBytes ?? DEFAULT_MAX_INVENTORY_BYTES),
  );
  let anchor: Anchor | null = null;
  for (let candidate = dudeHeaderEnd + 4; candidate + 4 <= limit; candidate += 4) {
    const found = tryAnchor(bytes, view, candidate, format);
    if (!found) continue;
    if (anchor) {
      throw new SaveParseError(
        `character block is ambiguous (plausible anchors at ${anchor.start} with ${anchor.partySlotCount} party slots and ${found.start} with ${found.partySlotCount})`,
        found.start,
      );
    }
    anchor = found;
  }
  if (!anchor) {
    throw new SaveParseError(
      'could not locate the character block (group 6) after the dude object; '
      + 'the save may have been taken during combat, which this codec does not model',
      dudeHeaderEnd,
    );
  }

  const centerTileStart = anchor.start - 4;
  const centerTile = view.getInt32(centerTileStart, false);
  // Fallout 1 already has the queue: the anchor had to walk it to reach the
  // traits. Fallout 2's is at the far end of the file and is found by anchoring
  // backwards from the fixed seven-byte interface group at EOF.
  const parsedQueue = anchor.queue
    ? { events: anchor.queue.events, layout: null, span: anchor.queue.span }
    : parseQueueFromTail(bytes, view, anchor.tailStart, format);

  const layout: SaveLayout = {
    header: { start: 0, end: HEADER_SIZE },
    dudeCid: { start: dudeCidStart, end: gvarsStart },
    gvars: { start: gvarsStart, end: gvarsEnd },
    mapList: { start: gvarsEnd + format.gvarTrailerBytes, end: solution.mirrorStart },
    gvarsMirror: { start: solution.mirrorStart, end: mirrorEnd },
    dudeObject: { start: dudeStart, end: dudeHeaderEnd },
    inventory: { start: dudeHeaderEnd, end: centerTileStart },
    centerTile: { start: centerTileStart, end: anchor.start },
    critter: { start: anchor.start, end: anchor.spans.critter.end },
    kills: anchor.spans.kills,
    taggedSkills: anchor.spans.taggedSkills,
    perks: anchor.spans.perks,
    combatState: anchor.spans.combatState,
    aiPackets: anchor.spans.aiPackets,
    pcStats: anchor.spans.pcStats,
    traits: anchor.spans.traits,
    automapFlags: anchor.spans.automapFlags,
    tail: { start: anchor.tailStart, end: bytes.length },
    queue: parsedQueue?.span ?? null,
  };

  return {
    game,
    format,
    byteLength: bytes.length,
    originalBytes: bytes.slice(),
    header,
    dudeCid,
    gvars,
    gvarTrailer,
    gvarMirrorTrailer,
    gvarsMirror,
    mapFiles: solution.mapList.mapFiles,
    automapDbSize: solution.mapList.automapDbSize,
    dude,
    inventoryBlob: bytes.slice(dudeHeaderEnd, centerTileStart),
    centerTile,
    sneakWorking: anchor.sneakWorking,
    critter: anchor.critter,
    kills: anchor.kills,
    taggedSkills: anchor.taggedSkills,
    perks: anchor.perks,
    partySlotCount: anchor.partySlotCount,
    combatState: anchor.combatState,
    aiPacketsBlob: anchor.aiPacketsBlob,
    pcStats: anchor.pcStats,
    traits: anchor.traits,
    automapFlags: anchor.automapFlags,
    tailBlob: bytes.slice(anchor.tailStart),
    queueEvents: parsedQueue?.events ?? null,
    queueLayout: parsedQueue?.layout ?? null,
    layout,
  };
}

function writeHeader(w: Writer, h: SaveHeader): void {
  w.push(h.signatureRaw);
  w.i16(h.versionMajor);
  w.i16(h.versionMinor);
  w.u8(h.versionRelease.charCodeAt(0));
  w.push(h.characterNameRaw);
  w.push(h.descriptionRaw);
  w.i16(h.fileDay);
  w.i16(h.fileMonth);
  w.i16(h.fileYear);
  w.i32(h.fileTime);
  w.i16(h.gameMonth);
  w.i16(h.gameDay);
  w.i16(h.gameYear);
  w.u32(h.gameTime);
  w.i16(h.elevation);
  w.i16(h.map);
  w.push(h.mapFileRaw);
  w.push(h.preview);
  w.push(h.trailer);
}

function writeDudeObject(w: Writer, d: DudeObject): void {
  w.i32(d.id);
  w.i32(d.tile);
  w.i32(d.x);
  w.i32(d.y);
  w.i32(d.sx);
  w.i32(d.sy);
  w.i32(d.frame);
  w.i32(d.rotation);
  w.i32(d.fid);
  w.u32(d.flags);
  w.i32(d.elevation);
  w.i32(d.pid);
  w.i32(d.cid);
  w.i32(d.lightDistance);
  w.i32(d.lightIntensity);
  w.i32(d.outline);
  w.i32(d.sid);
  w.i32(d.scriptIndex);
  w.i32(d.inventoryLength);
  w.i32(d.inventoryCapacity);
  w.i32(d.inventoryPointer);
  w.i32(d.dataFlags);
  w.i32(d.combatDamageLastTurn);
  w.i32(d.combatManeuver);
  w.i32(d.combatAp);
  w.i32(d.combatResults);
  w.i32(d.combatAiPacket);
  w.i32(d.combatTeam);
  w.i32(d.combatWhoHitMeCid);
  w.i32(d.hp);
  w.i32(d.radiation);
  w.i32(d.poison);
}

function writeQueue(w: Writer, events: readonly QueueEvent[], format: SaveFormat): void {
  w.i32(events.length);
  for (const event of events) {
    const words = format.queueEventDataWords[event.type];
    if (words === undefined || event.data.length !== words) {
      throw new SaveParseError(`cannot serialize queue event type ${event.type} with ${event.data.length} payload words`);
    }
    w.u32(event.time);
    w.i32(event.type);
    w.i32(event.ownerId);
    w.i32List(event.data);
  }
}

/**
 * Re-encode the save. Typed regions are written from the model; opaque regions
 * are copied verbatim. With no edits applied the result is byte-identical to the
 * input, which is the codec's contract and what `saveedit-roundtrip.mjs` proves.
 *
 * The section order below is `_master_save_list` / `master_save_list`, and the
 * three places it forks are the three the format table records: the GVAR
 * trailer byte, the critter block's `damageType` word, and where the queue goes.
 */
export function serializeSaveDat(save: SaveDat): Uint8Array {
  const format = save.format;
  const w = new Writer();

  writeHeader(w, save.header);
  w.i32(save.dudeCid);
  w.i32List(save.gvars);
  w.push(save.gvarTrailer);

  w.i32(save.mapFiles.length);
  for (const name of save.mapFiles) {
    const raw = new Uint8Array(name.length + 1);
    for (let i = 0; i < name.length; i++) raw[i] = name.charCodeAt(i) & 0xff;
    w.push(raw);
  }
  w.i32(save.automapDbSize);

  w.i32List(save.gvarsMirror ?? save.gvars);
  w.push(save.gvarMirrorTrailer);

  writeDudeObject(w, save.dude);
  w.push(save.inventoryBlob);
  w.i32(save.centerTile);

  w.i32(save.sneakWorking);
  w.i32(save.critter.flags);
  w.i32List(save.critter.baseStats);
  w.i32List(save.critter.bonusStats);
  w.i32List(save.critter.skills);
  w.i32(save.critter.bodyType);
  w.i32(save.critter.experience);
  w.i32(save.critter.killType);
  if (format.critterHasDamageType) w.i32(save.critter.damageType ?? 0);

  w.i32List(save.kills);
  w.i32List(save.taggedSkills);
  w.i32List(save.perks);
  w.i32(save.combatState);
  w.push(save.aiPacketsBlob);
  w.i32List(save.pcStats);
  if (format.queueBeforeTraits) {
    // Fallout 1: `item_save` writes nothing, then `queue_save` (group 15), then
    // the traits. The queue is always parsed on this path — the anchor could not
    // have found the traits otherwise — so there is no verbatim fallback.
    if (!save.queueEvents) {
      throw new SaveParseError('a Fallout 1 save cannot be re-encoded without its parsed queue');
    }
    writeQueue(w, save.queueEvents, format);
  }
  w.i32List(save.traits);
  w.i32(save.automapFlags);
  if (!format.queueBeforeTraits && save.queueEvents && save.queueLayout) {
    w.push(save.tailBlob.subarray(0, save.queueLayout.start));
    writeQueue(w, save.queueEvents, format);
    w.push(save.tailBlob.subarray(save.queueLayout.end));
  } else {
    w.push(save.tailBlob);
  }

  const encoded = w.finish();
  if (encoded.length !== save.originalBytes.length) {
    throw new SaveParseError(
      `serialization changed SAVE.DAT length (${save.originalBytes.length} to ${encoded.length})`,
    );
  }

  // Keep every unmodelled region byte-for-byte from the input. Only spans the
  // editor understands are copied from the independently encoded model.
  const out = save.originalBytes.slice();
  const copy = (span: Span): void => out.set(encoded.subarray(span.start, span.end), span.start);
  copy(save.layout.header);
  copy(save.layout.gvars);
  copy(save.layout.gvarsMirror);
  copy(save.layout.dudeObject);
  copy(save.layout.critter);
  copy(save.layout.kills);
  copy(save.layout.taggedSkills);
  copy(save.layout.perks);
  copy(save.layout.pcStats);
  // Fallout 1's queue is not inside the tail, so it needs its own copy. On
  // Fallout 2 `layout.queue` is a sub-range of `layout.tail` and copying it
  // again would be a no-op, but skipping it keeps the intent legible.
  if (save.format.queueBeforeTraits && save.layout.queue) copy(save.layout.queue);
  copy(save.layout.traits);
  copy(save.layout.tail);
  return out;
}

/**
 * Two fields inside the "opaque" tail that are not opaque at all.
 *
 * Groups 17 and 18 are the only fixed-size members of the tail, and they are the
 * two the editor cannot do its job without:
 *
 *   - Group 17 (`preferencesSave`, engine/src/preferences.cc) is exactly 80
 *     bytes — twenty big-endian words, and the *first* of them is
 *     `gPreferencesGameDifficulty1`. `preferencesLoad` ends with `_JustUpdate_()`,
 *     which pushes it into `settings.preferences.game_difficulty` — the very
 *     value `skillGetGameDifficultyModifier` reads. So the save does know what
 *     difficulty it was played on, and the editor no longer has to assume Normal.
 *   - Group 18 (`characterEditorSave`, engine/src/character_editor.cc:5745) is
 *     the five bytes straight after it: `int32 gCharacterEditorLastLevel`, then
 *     `uint8 gCharacterEditorHasFreePerk`.
 *
 * The last-level word is the one that matters most, and it is not a nicety.
 * `characterEditorUpdateLevel` runs a catch-up loop from `lastLevel + 1` to the
 * current level every time the character screen opens, granting a level's skill
 * points for each step. An editor that raises the level and hands out the skill
 * points itself, without moving this word, gets the player the points *twice* —
 * once here and once the next time they press the character button. Writing it
 * is what makes engine mode's level accrual honest rather than approximately
 * right.
 *
 * The tail stays a blob and is still written back verbatim; these read and write
 * through it in place, so a save with no edits still round-trips byte for byte.
 * The parser has already proved both offsets are in range — the anchor search
 * validates `at + 85 <= length` and range-checks the last level — but the guards
 * are here too, because a caller can reach this with any `SaveDat`.
 */
const TAIL_GAME_DIFFICULTY_OFFSET = 0;
const TAIL_LAST_LEVEL_OFFSET = 80;
const TAIL_FREE_PERK_OFFSET = 84;
const TAIL_KNOWN_BYTES = 85;

/** `GameDifficulty` (engine/src/game_config.h:43). */
export type GameDifficulty = 'easy' | 'normal' | 'hard';

const DIFFICULTIES: readonly GameDifficulty[] = ['easy', 'normal', 'hard'];

const tailView = (save: SaveDat): DataView | null =>
  save.tailBlob.length >= TAIL_KNOWN_BYTES
    ? new DataView(save.tailBlob.buffer, save.tailBlob.byteOffset, save.tailBlob.byteLength)
    : null;

/**
 * The difficulty this save was played on, or null when the tail is too short to
 * hold group 17 (which no save this parser accepted can be — but a caller may
 * hand us anything).
 */
export function gameDifficulty(save: SaveDat): GameDifficulty | null {
  const view = tailView(save);
  if (!view) return null;
  return DIFFICULTIES[view.getInt32(TAIL_GAME_DIFFICULTY_OFFSET, false)] ?? null;
}

/** `gCharacterEditorLastLevel` — the level the character screen last caught up to. */
export function characterEditorLastLevel(save: SaveDat): number | null {
  const view = tailView(save);
  return view ? view.getInt32(TAIL_LAST_LEVEL_OFFSET, false) : null;
}

/** Write `gCharacterEditorLastLevel`. Returns false when the tail is too short. */
export function setCharacterEditorLastLevel(save: SaveDat, level: number): boolean {
  const view = tailView(save);
  if (!view) return false;
  view.setInt32(TAIL_LAST_LEVEL_OFFSET, level, false);
  return true;
}

/** `gCharacterEditorHasFreePerk` — perk picks the game still owes the player. */
export function characterEditorPerksOwed(save: SaveDat): number | null {
  const view = tailView(save);
  return view ? view.getUint8(TAIL_FREE_PERK_OFFSET) : null;
}

/**
 * Write `gCharacterEditorHasFreePerk`. The engine's own setter masks to a byte
 * and refuses anything over `kMaxPerkOwed` (250, character_editor.cc:160), so
 * this does the same rather than writing a value the engine would not.
 */
export function setCharacterEditorPerksOwed(save: SaveDat, owed: number): boolean {
  const view = tailView(save);
  if (!view) return false;
  const masked = owed & 0xff;
  if (masked > 250) return false;
  view.setUint8(TAIL_FREE_PERK_OFFSET, masked);
  return true;
}

/** Byte ranges where two buffers differ. Used by the diff-locality tests. */
export function diffRanges(a: Uint8Array, b: Uint8Array): Span[] {
  const ranges: Span[] = [];
  const n = Math.min(a.length, b.length);
  let start = -1;
  for (let i = 0; i < n; i++) {
    if (a[i] !== b[i]) {
      if (start === -1) start = i;
    } else if (start !== -1) {
      ranges.push({ start, end: i });
      start = -1;
    }
  }
  if (start !== -1) ranges.push({ start, end: n });
  if (a.length !== b.length) ranges.push({ start: n, end: Math.max(a.length, b.length) });
  return ranges;
}
